import React, { useRef, useMemo } from 'react';
import type { Cart } from 'shared/types/cart';
import { useCookies } from 'next-client-cookies';
import { useTheme } from 'next-themes';
import Drawer from 'components/commercetools-ui/atoms/drawer';
import Image from 'components/commercetools-ui/atoms/image';
import Slideout from 'components/commercetools-ui/organisms/header/utility-section/components/slide-out';
import { useAddToCartOverlay } from 'context/add-to-cart-overlay';
import {
  trackBeginCheckout,
  trackAddShippingInfo,
  getGA4OrderContext,
  trackMenuNavigation,
  trackViewCart,
  mapLineItemToGA4ItemTotal,
  type GA4Item,
} from 'helpers/analytics/ga4Events';
import { DARK, LIGHT } from 'helpers/constants/events';
import { hasFavorites, hasProject } from 'helpers/has-favorites';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { mapCountry } from 'helpers/utils/mapCountry';
import { desktop } from 'helpers/utils/screensizes';
import { useRouter } from 'i18n/routing';
import { useFeatures } from 'providers/feature-flag';
import type { Wishlist } from 'types/entity/wishlist';
import { UtilityIcons } from './components/UtilityIcons';
import type { HeaderContentProps } from '../types/gmsb-header';
import MyAccountDropDown from './components/my-account-dropdown';
import TranslationDropDown from './components/translation-dropdown';
import { groupLineItemsBySku } from '../../cart/helpers';

export interface Props {
  carts?: Cart[];
  wishlist?: Wishlist;
  contentConfig: HeaderContentProps;
  showSearchBarMobile?: () => void;
  showSearchMobile?: boolean;
}

const UtilitySection: React.FC<Props> = ({ carts, wishlist, contentConfig, showSearchBarMobile, showSearchMobile }) => {
  const { resolvedTheme = 'light' } = useTheme();
  const [isDesktopSize] = useMediaQuery(desktop);
  const { lineItems: wishlistLineItems = [] } = wishlist || {};
  const router = useRouter();

  const cartButtonRef = useRef<HTMLButtonElement | null>(null);
  const {
    searchIconDark,
    searchIconLight,
    ariaLabelSearch,
    separatorIconDark,
    separatorIconLight,
    cartIconDark,
    cartIconLight,
    projectIconDark,
    projectIconLight,
    helpIconDark,
    helpIconLight,
    helpLink,
    projectsLink,
    ariaLabelProjects,
    ariaLabelHelp,
    ariaLabelCart,
    favouritesIconLight,
    favouritesIconDark,
    ariaLabelFavourites,
  } = contentConfig;
  const { setModalOpen, modalOpen } = useAddToCartOverlay();

  const { enableGA4, publicDeepLink } = useFeatures();
  const deepLinkUrl = publicDeepLink();

  const cartId = carts?.[0]?.cartId ?? '';
  const cartUrl = `${deepLinkUrl}frontastic/action/cart/getCart?cartId=${cartId}`;

  const isEmpty = useMemo(() => {
    return !carts?.some((cart) => (cart.lineItems?.length ?? 0) > 0);
  }, [carts]);

  const onCartClicked = () => {
    if (carts && carts.length > 0) {
      let totalValue = 0;
      let allItems: GA4Item[] = [];
      let p2rContext: { orderType: string; shippingInfo: string } | null = null;

      for (const cart of carts) {
        totalValue += (cart.sum?.centAmount ?? 0) / Math.pow(10, cart.sum?.fractionDigits ?? 2);
        const groupedLineItems = groupLineItemsBySku(cart.lineItems);
        const mappedItems = groupedLineItems.map((li) => mapLineItemToGA4ItemTotal(enableGA4(), li));

        allItems = [...allItems, ...mappedItems];

        if (!p2rContext) {
          const context = getGA4OrderContext(cart?.lineItems?.[0]?.distributionChannel?.key);
          if (context.orderType === 'P2R') {
            p2rContext = context as { orderType: string; shippingInfo: string };
          }
        }
      }

      const currency = carts.find((cart) => cart.sum?.currencyCode)?.sum?.currencyCode ?? 'USD';
      const primaryContext = p2rContext || getGA4OrderContext(carts[0]?.lineItems?.[0]?.distributionChannel?.key);

      const commonParams = {
        enableGA4: enableGA4(),
        currency,
        value: totalValue,
        cartId,
        cartUrl,
        items: allItems,
        orderType: primaryContext.orderType,
        shippingInfo: primaryContext.shippingInfo,
      };

      if (p2rContext) {
        trackBeginCheckout(commonParams);
        trackAddShippingInfo({
          ...commonParams,
          cartId,
          cartUrl,
          shippingTier: `P2R - ${p2rContext.shippingInfo}`,
        });
      }

      // The header opens all carts, so there is no single cart context for this event.
      // Previously, this was passing in carts?.lineItems?.[0]?.distributionChannel?.key,
      // but that was always undefined, because carts is an array
      const ga4OrderContext = getGA4OrderContext();

      trackViewCart({
        enableGA4: enableGA4(),
        currency,
        value: totalValue,
        // NOTE: This is the id of the first cart we come across. If there are
        // multiple carts, we aggregate items and values across all carts here,
        // but still only pass one cart id and url
        cartId,
        cartUrl,
        items: allItems,
        ...ga4OrderContext,
      });
    }

    setModalOpen?.(true);
  };
  const redirectToFavorites = () => {
    trackMenuNavigation('Header Quick Links', 'Favorites', '/favorites');
    router.push('/favorites');
  };
  const redirectToMyProject = () => {
    trackMenuNavigation('Header Quick Links', 'Draft Projects', '/my-project');
    router.push('/my-project');
  };

  const handleDrawerClose = () => {
    setModalOpen(false);
    cartButtonRef.current?.focus();
  };

  const cookies = useCookies();
  const locale = (cookies.get('locale') as string) || 'en-US';
  const countries = mapCountry(locale.split('-')[1]);

  const localesCount = (countries?.locales ?? []).length;

  return (
    <div className="flex-0 flex h-40 items-center">
      <div className={`flex flex-1 items-center gap-0 text-14 text-[#5C5C5C] md:gap-12`}>
        {isDesktopSize && (
          <>
            <div className="size-20">
              <button
                aria-label={ariaLabelFavourites}
                className="cart-icon h-20 cursor-pointer"
                data-ffid="btn-header-wishlist-icon"
                onClick={() => {
                  redirectToFavorites();
                }}
              >
                {wishlistLineItems.length > 0 && hasFavorites(wishlistLineItems) && (
                  <span className="cart-dot" data-testid="wishlist-dot" data-ffid="wishlist-dot"></span>
                )}
                <UtilityIcons
                  isDark={resolvedTheme == DARK}
                  darkIcon={favouritesIconDark}
                  lightIcon={favouritesIconLight}
                  ariaLabel={ariaLabelFavourites}
                  tabIndex={-1}
                  ffid={`favourites`}
                  isSpan
                />
              </button>
            </div>
            <button
              aria-label={ariaLabelProjects}
              className="cart-icon h-20 cursor-pointer"
              data-ffid="btn-header-my-projects-icon"
              onClick={() => {
                redirectToMyProject();
              }}
            >
              {wishlistLineItems.length > 0 && hasProject(wishlistLineItems) && (
                <span className="cart-dot" data-testid="wishlist-dot" data-ffid="wishlist-dot"></span>
              )}
              <UtilityIcons
                isDark={resolvedTheme == DARK}
                darkIcon={projectIconDark}
                lightIcon={projectIconLight}
                ariaLabel={ariaLabelProjects}
                tabIndex={-1}
                ffid={`my-projects`}
                link={projectsLink}
                isSpan
              />
            </button>

            <button
              ref={cartButtonRef}
              aria-label={ariaLabelCart}
              className="cart-icon h-20 cursor-pointer"
              data-ffid={`btn-cart-button`}
              data-testid="cart-icon"
              disabled={isEmpty}
              aria-disabled={isEmpty}
              onClick={() => {
                trackMenuNavigation('Header Quick Links', 'Mini Cart', '');
                if (!isEmpty) onCartClicked();
              }}
            >
              {!isEmpty && <span className="cart-dot" data-testid="cart-dot" data-ffid="cart-dot"></span>}
              <UtilityIcons
                isDark={resolvedTheme == DARK}
                darkIcon={cartIconDark}
                lightIcon={cartIconLight}
                ariaLabel={ariaLabelCart}
                tabIndex={-1}
                isSpan
                // link={cartLink}
              />
            </button>
            <UtilityIcons
              isDark={resolvedTheme == DARK}
              darkIcon={separatorIconDark}
              lightIcon={separatorIconLight}
              ariaLabel={'header-separator-1'}
              ffid={`header-separator-1`}
              isSpan
            />
            {localesCount > 1 && <TranslationDropDown contentConfig={contentConfig} />}
            <UtilityIcons
              isDark={resolvedTheme == DARK}
              darkIcon={helpIconDark}
              lightIcon={helpIconLight}
              ariaLabel={ariaLabelHelp}
              ffid={`help`}
              link={helpLink}
            />
            <UtilityIcons
              isDark={resolvedTheme == DARK}
              darkIcon={separatorIconDark}
              lightIcon={separatorIconLight}
              ariaLabel={'header-separator-2'}
              ffid={`header-separator-2`}
              isSpan
            />

            <MyAccountDropDown contentConfig={contentConfig} />
          </>
        )}
        {!isDesktopSize && (
          <>
            {resolvedTheme == DARK && (
              <button
                aria-label={ariaLabelSearch}
                data-ffid={`lnk-search-dark-theme`}
                style={{
                  width: searchIconDark?.media?.width,
                  height: searchIconDark?.media?.height,
                  marginRight: '16px',
                }}
                className={showSearchMobile ? 'rounded-full bg-[#f8f9fa]' : ''}
                onClick={() => showSearchBarMobile?.()}
                type="button"
              >
                <Image
                  media={searchIconDark?.media}
                  width={Number(searchIconDark?.media?.width)}
                  height={Number(searchIconDark?.media?.height)}
                  alt={ariaLabelSearch}
                  data-ffid={`img-search-icon-dark-theme`}
                />
              </button>
            )}
            {resolvedTheme == LIGHT && (
              <button
                aria-label={ariaLabelSearch}
                data-ffid={`lnk-search-light-theme`}
                style={{
                  width: searchIconLight?.media?.width,
                  height: searchIconLight?.media?.height,
                  marginRight: '16px',
                }}
                className={showSearchMobile ? 'rounded-full bg-[#f8f9fa]' : ''}
                onClick={() => showSearchBarMobile?.()}
                type="button"
              >
                <span
                  className={`size-24 ${showSearchMobile ? 'bg-[#0B57D0]' : 'bg-[#333]'} inline-block`}
                  data-ffid={`img-search-icon-light-theme`}
                  style={{
                    maskImage: `url(${searchIconLight?.media?.file})`,
                    WebkitMaskImage: `url(${searchIconLight?.media?.file})`,
                    maskRepeat: 'no-repeat',
                    WebkitMaskRepeat: 'no-repeat',
                    maskSize: 'contain',
                    WebkitMaskSize: 'contain',
                    maskPosition: 'center',
                    WebkitMaskPosition: 'center',
                  }}
                />
              </button>
            )}
            <button
              ref={cartButtonRef}
              aria-label={ariaLabelCart}
              className="cursor-pointer border-gray-600"
              data-testid="cart-icon"
              onClick={() => {
                trackMenuNavigation('Header Quick Links', 'Mini Cart', '');
                if (!isEmpty) onCartClicked();
              }}
              data-ffid="lnk-cart"
            >
              <span className="cart-icon block" data-testid="cart-dot">
                {!isEmpty && <span className="cart-dot" data-ffid="cart-dot"></span>}
                <UtilityIcons
                  isDark={resolvedTheme == DARK}
                  darkIcon={cartIconDark}
                  lightIcon={cartIconLight}
                  ariaLabel={ariaLabelCart}
                  tabIndex={-1}
                  ffid={`cart`}
                  isSpan
                />
              </span>
            </button>
          </>
        )}
      </div>
      <Drawer
        isOpen={modalOpen}
        direction={`${isDesktopSize ? 'right' : 'left'}`}
        className={`w-[90%] max-w-[430px] bg-white ${isDesktopSize ? 'absolute mt-56' : ''} `}
        onClose={handleDrawerClose}
        isDisableCartOverlay
      >
        <div className="bg-white">
          <Slideout carts={carts} onClose={handleDrawerClose} contentConfig={contentConfig} />
        </div>
      </Drawer>
    </div>
  );
};

export default UtilitySection;
